/**
 * StockfishAnalysisRunner.js - Handles actual Stockfish engine communication
 * Manages Web Worker, UCI protocol, and result parsing
 */

import { Chess } from 'chess.js';

class StockfishAnalysisRunner {
  constructor() {
    this.worker = null;
    this.isReady = false;
    this.currentAnalysis = null;
    this.messageQueue = [];
    this.pendingAnalysis = null;
  }
  
  /**
   * Initialize the Stockfish Web Worker
   */
  async initialize() {
    if (this.worker) {
      return Promise.resolve();
    }
    
    return new Promise((resolve, reject) => {
      try {
        // Try different worker paths
        const workerPaths = [
          '/stockfish-nnue-16-single.js',
          './stockfish-nnue-16-single.js',
          `${process.env.PUBLIC_URL || ''}/stockfish-nnue-16-single.js`
        ];
        
        let workerCreated = false;
        
        for (const path of workerPaths) {
          try {
            this.worker = new Worker(path);
            workerCreated = true;
            break;
          } catch (e) {
            console.warn(`⚠️ StockfishAnalysisRunner: Failed to create worker with path ${path}:`, e.message);
          }
        }
        
        if (!workerCreated) {
          throw new Error('Could not create Stockfish worker with any path');
        }
        
        // Set up message handler
        this.worker.onmessage = (event) => {
          this.handleWorkerMessage(event.data);
        };
        
        this.worker.onerror = (error) => {
          console.error('❌ StockfishAnalysisRunner: Worker error:', error);
          reject(error);
        };
        
        // Initialize engine
        this.sendCommand('uci');
        
        // Wait for readyok before resolving
        const readyTimeout = setTimeout(() => {
          reject(new Error('Stockfish initialization timeout'));
        }, 10000);
        
        const readyHandler = (message) => {
          if (message === 'readyok') {
            clearTimeout(readyTimeout);
            this.isReady = true;
            resolve();
          }
        };
        
        // Temporarily add ready handler
        const originalHandler = this.handleWorkerMessage;
        this.handleWorkerMessage = (message) => {
          readyHandler(message);
          originalHandler.call(this, message);
        };
        
        // Configure engine settings
        setTimeout(() => {
          this.sendCommand('setoption name MultiPV value 3');
          this.sendCommand('setoption name Threads value 1');
          this.sendCommand('setoption name Hash value 128');
          this.sendCommand('setoption name Use NNUE value false');
          this.sendCommand('isready');
        }, 500);
        
      } catch (error) {
        console.error('❌ StockfishAnalysisRunner: Initialization failed:', error);
        reject(error);
      }
    });
  }
  
  /**
   * Send command to Stockfish engine
   */
  sendCommand(command) {
    if (!this.worker) {
      console.error('❌ StockfishAnalysisRunner: Cannot send command, worker not initialized');
      return;
    }
    
    // console.log(`📤 StockfishAnalysisRunner: Sending command: ${command}`);
    this.worker.postMessage(command);
  }
  
  /**
   * Handle messages from Stockfish worker
   */
  handleWorkerMessage(message) {
    if (typeof message !== 'string') {
      return;
    }
    
    // console.log(`📥 StockfishAnalysisRunner: Received: ${message}`);
    
    if (message === 'readyok') {
      this.isReady = true;
      return;
    }
    
    // Process analysis info
    if (message.includes('info depth') && this.currentAnalysis) {
      this.processAnalysisInfo(message);
    }
    
    // Handle analysis completion
    if (message.startsWith('bestmove') && this.currentAnalysis) {
      this.completeAnalysis();
    }
  }
  
  /**
   * Process UCI info messages during analysis
   */
  processAnalysisInfo(message) {
    if (!this.currentAnalysis) return;
    
    const matches = {
      depth: message.match(/depth (\d+)/),
      multipv: message.match(/multipv (\d+)/),
      scoreType: message.match(/score (cp|mate) (-?\d+(\.\d+)?)/),
      pv: message.match(/pv (.+)$/)
    };
    
    if (!matches.depth || !matches.multipv || !matches.scoreType || !matches.pv) {
      return;
    }
    
    const depth = parseInt(matches.depth[1]);
    const multipv = parseInt(matches.multipv[1]);
    const scoreType = matches.scoreType[1];
    const rawScoreValue = matches.scoreType[2];
    const pvString = matches.pv[1].trim();
    const pvMoves = pvString.split(' ').filter(move => 
      move.length >= 4 && move.match(/^[a-h][1-8][a-h][1-8][qrbn]?$/)
    );
    
    if (pvMoves.length === 0) {
      return;
    }
    
    // Calculate normalized score
    const { normalizedScore, normalizedCP } = this.normalizeScore(
      scoreType, 
      rawScoreValue, 
      this.currentAnalysis.fen
    );
    
    // Convert best move to SAN
    const bestMoveLAN = pvMoves[0];
    const bestMoveSAN = this.convertLANtoSAN(bestMoveLAN, this.currentAnalysis.fen);
    const pvSAN = this.convertPVtoSAN(pvMoves, this.currentAnalysis.fen);
    
    // Update analysis data
    this.currentAnalysis.depth = Math.max(this.currentAnalysis.depth, depth);
    
    // Ensure lines array is big enough
    while (this.currentAnalysis.lines.length < multipv) {
      this.currentAnalysis.lines.push(null);
    }
    
    // Update the specific line
    this.currentAnalysis.lines[multipv - 1] = {
      line: multipv,
      evaluation: normalizedScore,
      normalizedCP: normalizedCP,
      bestMoveLAN: bestMoveLAN,
      bestMoveSAN: bestMoveSAN,
      pv: pvMoves,
      pvSAN: pvSAN
    };
    
    // DISABLED: Send partial updates for responsive UI to prevent unnecessary re-renders
    // Only send final update when analysis is complete to eliminate flicker
    // if (depth === 10 || depth >= this.currentAnalysis.targetDepth - 2) {
    //   this.sendPartialUpdate();
    // }
  }
  
  /**
   * Normalize score to user perspective
   */
  normalizeScore(scoreType, rawScoreValue, fen) {
    const isBlackToMove = fen.split(' ')[1] === 'b';
    
    // Get user orientation (default to white)
    let userOrientation = 'white';
    try {
      if (window.state?.orientation) {
        userOrientation = window.state.orientation;
      } else if (window.getGameState && typeof window.getGameState === 'function') {
        const gameState = window.getGameState();
        userOrientation = gameState?.orientation || 'white';
      }
    } catch (e) {
      // Default to white
    }
    
    const userPlayingWhite = userOrientation === 'white';
    
    if (scoreType === 'cp') {
      let cpScore = parseInt(rawScoreValue) / 100;
      
      // Normalize to user's perspective
      let normalizedCP;
      if (userPlayingWhite) {
        normalizedCP = isBlackToMove ? -cpScore : cpScore;
      } else {
        normalizedCP = isBlackToMove ? cpScore : -cpScore;
      }
      
      const normalizedScore = normalizedCP >= 0 ? `+${normalizedCP.toFixed(2)}` : normalizedCP.toFixed(2);
      return { normalizedScore, normalizedCP };
    } else if (scoreType === 'mate') {
      let rawMateIn = parseInt(rawScoreValue);
      let normalizedMateIn;
      
      if (userPlayingWhite) {
        normalizedMateIn = isBlackToMove ? -rawMateIn : rawMateIn;
      } else {
        normalizedMateIn = isBlackToMove ? rawMateIn : -rawMateIn;
      }
      
      const normalizedScore = `M${normalizedMateIn}`;
      return { normalizedScore, normalizedCP: normalizedScore };
    }
    
    return { normalizedScore: '0.00', normalizedCP: 0 };
  }
  
  /**
   * Convert LAN move to SAN using Chess.js
   */
  convertLANtoSAN(lanMove, fen) {
    try {
      const chess = new Chess(fen);
      const moveResult = chess.move(lanMove);
      return moveResult ? moveResult.san : lanMove;
    } catch (error) {
      console.warn(`⚠️ StockfishAnalysisRunner: Could not convert ${lanMove} to SAN:`, error);
      return lanMove;
    }
  }
  
  /**
   * Convert PV array from LAN to SAN
   */
  convertPVtoSAN(pvLAN, fen) {
    try {
      const chess = new Chess(fen);
      const sanMoves = [];
      
      for (const lanMove of pvLAN) {
        const moveResult = chess.move(lanMove);
        if (moveResult) {
          sanMoves.push(moveResult.san);
        } else {
          break;
        }
      }
      
      return sanMoves;
    } catch (error) {
      console.warn('⚠️ StockfishAnalysisRunner: Error converting PV to SAN:', error);
      return [];
    }
  }
  
  /**
   * Send partial analysis update
   */
  sendPartialUpdate() {
    if (!this.currentAnalysis || !this.currentAnalysis.onProgress) {
      return;
    }
    
    const validLines = this.currentAnalysis.lines.filter(line => line !== null);
    if (validLines.length === 0) {
      return;
    }
    
    const partialResult = {
      depth: this.currentAnalysis.depth,
      positionFEN: this.currentAnalysis.fen,
      timestamp: Date.now(),
      pvs: validLines,
      isComplete: false
    };
    
    this.currentAnalysis.onProgress(partialResult);
  }
  
  /**
   * Complete current analysis
   */
  completeAnalysis() {
    if (!this.currentAnalysis) {
      return;
    }
    
    const validLines = this.currentAnalysis.lines.filter(line => line !== null);
    
    const finalResult = {
      depth: this.currentAnalysis.depth,
      positionFEN: this.currentAnalysis.fen,
      timestamp: Date.now(),
      pvs: validLines,
      isComplete: true
    };
    
    
    const callback = this.currentAnalysis.resolve;
    this.currentAnalysis = null;
    
    if (callback) {
      callback(finalResult);
    }
  }
  
  /**
   * Analyze a position
   * @param {string} fen - Position to analyze
   * @param {Object} options - Analysis options (depth, multiPV, signal)
   */
  async analyzePosition(fen, options = {}) {
    if (!this.isReady) {
      throw new Error('Stockfish engine not ready');
    }
    
    const { depth = 15, multiPV = 3, signal, onProgress } = options;
    
    return new Promise((resolve, reject) => {
      // Check if cancelled before starting
      if (signal && signal.aborted) {
        reject(new DOMException('Analysis aborted', 'AbortError'));
        return;
      }
      
      // Stop any current analysis
      this.stopAnalysis();
      
      // Set up new analysis
      this.currentAnalysis = {
        fen,
        targetDepth: depth,
        depth: 0,
        lines: [],
        resolve,
        reject,
        onProgress,
        startTime: Date.now()
      };
      
      // Handle abort signal
      if (signal) {
        signal.addEventListener('abort', () => {
          // console.log('🛑 StockfishAnalysisRunner: Analysis aborted via signal');
          this.stopAnalysis();
          reject(new DOMException('Analysis aborted', 'AbortError'));
        });
      }
      
      // Start analysis
      // console.log(`🚀 StockfishAnalysisRunner: Starting analysis - FEN: ${fen.substring(0, 30)}..., Depth: ${depth}`);
      
      this.sendCommand('ucinewgame');
      this.sendCommand(`position fen ${fen}`);
      this.sendCommand(`go depth ${depth}`);
      
      // Set timeout as fallback
      setTimeout(() => {
        if (this.currentAnalysis && this.currentAnalysis.fen === fen) {
          // console.log('⏰ StockfishAnalysisRunner: Analysis timeout, forcing completion');
          this.completeAnalysis();
        }
      }, 30000); // 30 second timeout
    });
  }
  
  /**
   * Stop current analysis
   */
  stopAnalysis() {
    if (this.currentAnalysis) {
      // console.log('🛑 StockfishAnalysisRunner: Stopping current analysis');
      this.sendCommand('stop');
      
      // Reject the pending promise
      if (this.currentAnalysis.reject) {
        this.currentAnalysis.reject(new DOMException('Analysis stopped', 'AbortError'));
      }
      
      this.currentAnalysis = null;
    }
  }
  
  /**
   * Check if currently analyzing
   */
  isAnalyzing() {
    return this.currentAnalysis !== null;
  }
  
  /**
   * Destroy the runner and clean up resources
   */
  destroy() {
    this.stopAnalysis();
    
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    
    this.isReady = false;
    
  }
}

export default StockfishAnalysisRunner; 